import { useState } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import { extractOrderItems } from '../../utils/StringUtils';
import { AiOutlineLoading, AiOutlineInbox } from 'react-icons/ai';
import { BsCupHot, BsCookie, BsMoon } from 'react-icons/bs';
import { FaUtensils } from "react-icons/fa6";
import { useAuth } from '../../auth/AuthenticationWrapper';
import { useReservationsLogic } from './hooks/useReservationsLogic';
import { CancelReservationModal } from './components/cancelReservationModal';
import { ConfirmCancelReservationModal } from './components/confirmCancelReservationModal';
import { ReservationDetailsModal } from './components/reservationDetailsModal';

const TabButton = ({ active, onClick, label, shortLabel }) => (
    <button
        onClick={onClick}
        className={`flex-1 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all border-b-4
            ${active ? 'border-custom_blue text-custom_blue bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
    >
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{shortLabel || label}</span>
    </button>
);

const StatusBadge = ({ status }) => {
    const map = {
        PENDING: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-700' },
        RELEASED: { label: 'Liberada', className: 'bg-blue-100 text-blue-700' },
        CANCELED: { label: 'Cancelada', className: 'bg-red-100 text-red-600' },
    };
    const { label, className } = map[status] || { label: status, className: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${className}`}>{label}</span>;
};

const TurnIcon = ({ name }) => {
    const n = (name || '').toLowerCase();
    if (n.includes('café') || n.includes('manha') || n.includes('manhã'))
        return <BsCupHot className="text-orange-400" size={18} />;
    if (n.includes('almoço') || n.includes('almoco'))
        return <FaUtensils className="text-red-400" size={18} />;
    if (n.includes('lanche') || n.includes('tarde'))
        return <BsCookie className="text-purple-400" size={18} />;
    if (n.includes('jantar') || n.includes('noite'))
        return <BsMoon className="text-indigo-400" size={18} />;
    return null;
};

const ReservationCard = ({ reservation, onDetails, onCancel, canManage, isParent }) => {
    const studentName = reservation.studentId?.split(' / ')[1] || reservation.studentId;
    const formattedTotal = reservation.total != null
        ? `R$ ${reservation.total.toFixed(2).replace('.', ',')}`
        : '-';
    const items = extractOrderItems(reservation.itemsList);

    if (isParent) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden active:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3 p-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <p className="font-bold text-gray-800 text-base leading-tight truncate">{studentName}</p>
                            <StatusBadge status={reservation.status} />
                        </div>
                        <p className="text-sm text-gray-500">
                            {items.length} {items.length === 1 ? 'item' : 'itens'} · <span className="font-semibold text-gray-700">{formattedTotal}</span>
                        </p>
                        {items.length > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                                {items.map(i => `${i.name} x${i.quantity}`).join(', ')}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex border-t border-slate-100">
                    <button
                        onClick={() => onDetails(reservation)}
                        className="flex-1 py-3 text-sm font-bold text-custom_blue bg-blue-50 hover:bg-blue-100 transition-colors"
                    >
                        Detalhes
                    </button>
                    {reservation.status === 'PENDING' && (
                        <>
                            <div className="w-px bg-slate-100" />
                            <button
                                onClick={() => onCancel(reservation)}
                                className="flex-1 py-3 text-sm font-bold text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
                            >
                                Cancelar
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-xs text-gray-400 font-bold uppercase">#{reservation.reservationId}</p>
                    <p className="font-bold text-gray-800 text-sm mt-0.5">{studentName}</p>
                </div>
                <StatusBadge status={reservation.status} />
            </div>

            <div className="text-xs text-gray-500 flex flex-col gap-1">
                <span>{items.length} {items.length === 1 ? 'item' : 'itens'} · {formattedTotal}</span>
                {reservation.paymentMethod && <span>{reservation.paymentMethod}</span>}
            </div>

            <div className="flex gap-2 pt-1">
                <button
                    onClick={() => onDetails(reservation)}
                    className="flex-1 py-2 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-gray-700 rounded-lg transition-colors"
                >
                    Detalhes
                </button>
                {reservation.status === 'PENDING' && canManage && (
                    <button
                        onClick={() => onCancel(reservation)}
                        className="flex-1 py-2 text-xs font-bold bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                    >
                        Cancelar
                    </button>
                )}
            </div>
        </div>
    );
};

const TurnSection = ({ turnName, reservations, onDetails, onCancel, canManage, isParent }) => (
    <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
            <TurnIcon name={turnName} />
            <span className="font-bold text-gray-700 text-sm">{turnName}</span>
            <span className="bg-slate-100 text-slate-500 text-xs font-bold px-2 py-0.5 rounded-full">{reservations.length}</span>
            <div className="h-px bg-slate-200 flex-1" />
        </div>
        <div className={isParent
            ? 'flex flex-col gap-2'
            : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
        }>
            {reservations.map(r => (
                <ReservationCard
                    key={r.reservationId}
                    reservation={r}
                    onDetails={onDetails}
                    onCancel={onCancel}
                    canManage={canManage}
                    isParent={isParent}
                />
            ))}
        </div>
    </div>
);

function ReservationsTable({ mode = 'admin' }) {
    const { hasPermission } = useAuth();
    const canManage = hasPermission('reservations', 'manage');
    const isParent = mode === 'parent';

    const { data, actions } = useReservationsLogic({ mode });
    const { groupedByTurn, groupedByDateAndTurn, loading, selectedDate, statusFilter } = data;

    const [selectedReservation, setSelectedReservation] = useState(null);
    const [modalType, setModalType] = useState(null);

    const handleDetails = (r) => { setSelectedReservation(r); setModalType('details'); };
    const handleCancel = (r) => { setSelectedReservation(r); setModalType('cancelCheck'); };
    const handleCheckConfirm = () => {
        if (isParent) {
            actions.cancelReservation(selectedReservation);
            setModalType(null);
        } else {
            setModalType('cancel');
        }
    };
    const handleConfirmCancel = (reservation) => { actions.cancelReservation(reservation); setModalType(null); };

    const isEmpty = isParent
        ? !groupedByDateAndTurn || groupedByDateAndTurn.length === 0
        : groupedByTurn.length === 0;

    return (
        <div className='mx-4 sm:mx-10 my-6 flex-1 font-poppins text-custom_lighter_gray_font'>

            <div className="flex flex-col h-full bg-slate-50 font-poppins">
                {/* Filter bar */}
                <div className="bg-white shadow-sm sticky top-0 z-20">
                    <div className="p-3 border-b border-slate-100 flex justify-end">
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg hover:border-custom_blue transition-all">
                            <MdCalendarToday className="text-custom_blue" size={18} />
                            <input
                                type="date"
                                className="bg-transparent text-sm font-bold text-gray-600 outline-none cursor-pointer"
                                value={selectedDate}
                                onChange={(e) => actions.setSelectedDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex w-full border-b border-slate-100">
                        <TabButton active={statusFilter === 'all'} onClick={() => actions.setStatusFilter('all')} label="Todas" shortLabel="Todas" />
                        <TabButton active={statusFilter === 'PENDING'} onClick={() => actions.setStatusFilter('PENDING')} label="Pendentes" shortLabel="Pend." />
                        <TabButton active={statusFilter === 'RELEASED'} onClick={() => actions.setStatusFilter('RELEASED')} label="Liberadas" shortLabel="Lib." />
                        <TabButton active={statusFilter === 'CANCELED'} onClick={() => actions.setStatusFilter('CANCELED')} label="Canceladas" shortLabel="Canc." />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <AiOutlineLoading className="animate-spin text-4xl mb-2 text-custom_blue" />
                            <p>Carregando...</p>
                        </div>
                    ) : isEmpty ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <AiOutlineInbox size={48} className="mb-2 opacity-20" />
                            <p>Nenhuma reserva encontrada.</p>
                        </div>
                    ) : isParent ? (
                        groupedByDateAndTurn.map(({ date, turns }) => (
                            <div key={date} className="mb-8">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="bg-white border border-slate-200 text-slate-600 px-4 py-1 rounded-full text-sm font-bold shadow-sm">
                                        {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                                    </span>
                                    <div className="h-px bg-slate-200 flex-1" />
                                </div>
                                {turns.map((turn, i) => (
                                    <TurnSection
                                        key={i}
                                        turnName={turn.turnName}
                                        reservations={turn.reservations}
                                        onDetails={handleDetails}
                                        onCancel={handleCancel}
                                        canManage={canManage}
                                        isParent={isParent}
                                    />
                                ))}
                            </div>
                        ))
                    ) : (
                        groupedByTurn.map((turn, i) => (
                            <TurnSection
                                key={i}
                                turnName={turn.turnName}
                                reservations={turn.reservations}
                                onDetails={handleDetails}
                                onCancel={handleCancel}
                                canManage={canManage}
                                isParent={isParent}
                            />
                        ))
                    )}
                </div>

                <ConfirmCancelReservationModal
                    isOpen={modalType === 'cancelCheck'}
                    onClose={() => setModalType(null)}
                    reservation={selectedReservation}
                    onConfirm={handleCheckConfirm}
                />

                <CancelReservationModal
                    isOpen={modalType === 'cancel'}
                    onClose={() => setModalType(null)}
                    reservation={selectedReservation}
                    loading={loading}
                    onConfirm={handleConfirmCancel}
                    requirePin={!isParent}
                />

                <ReservationDetailsModal
                    isOpen={modalType === 'details'}
                    onClose={() => setModalType(null)}
                    reservation={selectedReservation}
                    onCancel={handleCancel}
                    canManage={canManage}
                    isParent={isParent}
                />
            </div>
        </div>
    );
}

export default ReservationsTable;
